import { Component, lazy, Suspense, useEffect, useRef, useState } from 'react'
import { BOARD_PRESETS, C, ELEVATION, F, GHOST, PRIMARY, R, SECONDARY_COMPACT, T, TONES, chip, surface } from './theme.js'
import { continueAnonymously, currentUser, signOutUser, subscribeAuth } from './auth.js'
import { analyticsConsent, setAnalyticsConsent, trackEvent } from './firebase.js'
import { submitFeedback } from './feedback.js'
import { AuthPanel } from './AuthPanel.jsx'
import { authInput, authPrimary, authSecondary, authLink, authError } from './authStyles.js'
import { loadLearn, saveLearn } from './learn/persist.js'
import { getLocale, initLocale, SUPPORTED_LOCALES, t, useT } from './i18n.js'
/* Static rather than lazy: it imports only theme.js and Sheet.jsx, both
   already in this chunk, so it costs about a kilobyte and no catalog data —
   the split that lazyChunks.test.js protects is unaffected. */
import NotationKey from './learn/NotationKey.jsx'
import DrillHelp from './learn/DrillHelp.jsx'
import { loadRemoteAppState, saveRemoteAppState } from './ledgerSync.js'
import { onHardwareBack, platform } from './native.js'
import { noteLanding, playStoreUrl, readAttribution } from './attribution.js'
import { classifyUserAgent, noteLandingAct, pauseLandingVisit, startLandingVisit } from './landingVisit.js'
import { LANDING_VERSION, heroImage, landingAd, landingImage, moreFeatures } from './adLanding.js'
import { attachNotifyPrefs, PREFS_CHANGED, syncReminders } from './notify.js'
import { WEB_HINT_KEY, hasBillingAccount, isProUser, refreshEntitlements, subscribePro } from './entitlements.js'
import { WEB_CHECKOUT, openBillingPortal } from './creemCheckout.js'
import { HANDOFF_OPEN } from './proHandoff.js'
import { useIsMobile } from './hooks.js'
import { Sheet } from './Sheet.jsx'
import { sheetSection, SETTINGS_SLOT_ID } from './sheetStyles.js'
import NotifySettings from './NotifySettings.jsx'
import ProModal from './ProModal.jsx'
import TabIcon from './TabIcon.jsx'

// Each pulls in its own heavy vendor lib (recharts, chess.js + react-chessboard)
// so only the active tab's code and dependencies are fetched.
const OpeningLedger = lazy(() => import('./OpeningLedger.jsx'))
const LearnCenter = lazy(() => import('./learn/LearnCenter.jsx'))
// Both halves of the delete-account flow, sharing one chunk that the main
// bundle never pays for: the Firestore delete walk and the games-cache wipe
// are dead weight for everyone who isn't leaving.
const DeleteAccountSection = lazy(() => import('./DeleteAccount.jsx').then((m) => ({ default: m.DeleteAccountSection })))
const DeleteAccountPage = lazy(() => import('./DeleteAccount.jsx').then((m) => ({ default: m.DeleteAccountPage })))
// Reached only from a link in an email, so nobody browsing the app should pay
// for it — and it must render for a signed-OUT visitor, hence its own route
// above the auth gate rather than a panel inside Settings.
const UnsubscribePage = lazy(() => import('./Unsubscribe.jsx'))
// Open source licenses: opened from Settings and the landing footer, and a
// plain /licenses URL for the store listings. Lazy for the same reason.
const LicensesPage = lazy(() => import('./Licenses.jsx'))

/* Duplicated rather than imported from DeleteAccount.jsx on purpose: importing
 * it would pull that lazy chunk into the main bundle and defeat the split. */
const DELETED_FLAG = 'account-deleted'

/* Set by creemCheckout.js's success_url when Creem sends the browser back
 * from a completed checkout. Same treatment as DELETED_FLAG: read once, then
 * dropped from the URL immediately so a bookmark or reload can't re-trigger
 * the "confirming your purchase" screen. */
const PRO_SUCCESS_FLAG = 'pro'

/* Each language named in ITSELF, which is the convention every OS and browser
 * uses: someone looking for Spanish is scanning for "Español", and would not
 * necessarily recognise "Spanish". Not translated, deliberately — a language
 * list that changes language with the app is a list you cannot navigate back
 * out of once you have picked the wrong one. */
const LOCALE_LABEL = { en: 'English', es: 'Español' }

function TabFallback() {
  return (
    <div style={{ ...T.label, color: C.sub, display: 'grid', minHeight: '60vh', placeItems: 'center' }}>
      LOADING…
    </div>
  )
}

/* Android hardware back: while `active`, a press runs `handler` instead of
 * leaving the app. Registration order = stacking order (last active wins). */
function useHardwareBack(active, handler) {
  useEffect(() => {
    if (!active) return undefined
    return onHardwareBack(() => {
      handler()
      return true
    })
  }, [active]) // eslint-disable-line react-hooks/exhaustive-deps -- handlers only close UI; latest identity not needed
}

/* `raised` clears the mobile bottom tab bar so the bar stays tappable. */
function AnalyticsPrompt({ onChoose, onOpenPrivacy, raised = false, translate }) {
  return (
    <aside style={{ ...surface(), boxShadow: ELEVATION.overlay, bottom: `calc(${raised ? 70 : 14}px + var(--safe-bottom, 0px))`, color: C.ink, left: '50%', maxWidth: 520, padding: 14, position: 'fixed', transform: 'translateX(-50%)', width: 'calc(100% - 28px)', zIndex: 60 }}>
      <div style={{ ...T.title, fontSize: 17, marginBottom: 3 }}>{translate('app.analyticsTitle')}</div>
      <div style={{ ...T.caption, color: C.sub, marginBottom: 10 }}>{translate('app.analyticsBody')}</div>
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button onClick={() => onChoose(true)} style={authPrimary}>{translate('app.analyticsAllow')}</button>
        <button onClick={() => onChoose(false)} style={authSecondary}>{translate('app.analyticsNo')}</button>
        <button onClick={onOpenPrivacy} style={authLink}>{translate('app.privacy')}</button>
      </div>
    </aside>
  )
}

function AuthModal({ onClose, translate }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'grid', placeItems: 'center', background: 'rgba(10,15,12,0.72)', padding: 16 }}>
      <div onClick={(event) => event.stopPropagation()} style={{ ...surface(), boxShadow: ELEVATION.overlay, color: C.ink, padding: 20, width: 'min(100%, 360px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ ...T.title, fontSize: 21 }}>{translate('app.keepProgress')}</div>
          <button onClick={onClose} aria-label={translate('app.closeSignIn')} style={authLink}>{translate('app.close')}</button>
        </div>
        <AuthPanel onComplete={onClose} surface="account_modal" />
      </div>
    </div>
  )
}


/* Website only: inside the Android app the badge would advertise the app the
 * user is already holding, and an iPhone cannot install from Google Play. The
 * badge is Google's own artwork, trimmed of its transparent margin and
 * self-hosted, in the reader's language. The referrer's UTM tags land in Play
 * Console's acquisition report, the only place an install from here can be
 * told apart. */
function LandingPlayBadge({ signedIn, ios, translate }) {
  if (platform !== 'web' || ios) return null
  return (
    <a
      href={playStoreUrl(readAttribution())}
      target="_blank"
      rel="noopener"
      data-testid="landing-google-play"
      onClick={() => {
        trackEvent('google_play_clicked', { surface: signedIn ? 'landing_signed_in' : 'landing' })
        noteLandingAct('play')
      }}
      style={{ display: 'inline-block', marginTop: 16 }}
    >
      <img src={getLocale() === 'es' ? '/badges/google-play-es.png' : '/badges/google-play-en.png'} alt={translate('app.getOnGooglePlay')} height="48" style={{ display: 'block', height: 48, width: 'auto' }} />
    </a>
  )
}

/* The landing's pictures (adLanding.js). A card the app draws on its own sits
 * on the page as it is, 420px at most so its text is not drawn bigger than
 * the page's (it was rendered 400px wide). A phone screen goes in a phone
 * frame: a dark bezel round enough to read as a device at a glance, so the
 * app's buttons inside it are not taken for buttons on the page. As the first
 * picture a frame is cut short and fades out, which keeps the start button
 * under it on a phone's first screen. */
const BEZEL = 7
const FADE = 'linear-gradient(to bottom, #000 68%, transparent)'
/* The composite ad's three steps, in its order (adLanding.js#LANDING_FEATURES). */
const STEPS = ['read', 'find', 'train']
function LandingShot({ shot, hero = false, imgRef, isMobile }) {
  const img = (
    <img
      ref={imgRef}
      src={shot.src}
      srcSet={shot.srcSet}
      sizes={shot.sizes}
      width={shot.width}
      height={shot.height}
      alt=""
      {...(hero ? { fetchPriority: 'high' } : { loading: 'lazy' })}
      style={{ display: 'block', height: 'auto', maxWidth: shot.framed ? undefined : 420, width: '100%', ...(shot.fade ? { maskImage: FADE, WebkitMaskImage: FADE } : null) }}
    />
  )
  if (!shot.framed) return img
  const fade = FADE
  const cut = hero ? { maskImage: fade, maxHeight: isMobile ? '46vh' : '62vh', overflow: 'hidden', WebkitMaskImage: fade } : null
  return (
    <div style={{ background: C.ink, borderRadius: R.device, boxShadow: ELEVATION.overlay, boxSizing: 'border-box', margin: '0 auto', padding: BEZEL, width: hero ? 'min(250px, 66vw)' : 'min(280px, 72vw)', ...cut }}>
      <div style={{ borderRadius: R.device - BEZEL, overflow: 'hidden' }}>{img}</div>
    </div>
  )
}
// i18n-ignore — CSS grid areas, not copy.
const HERO_AREAS = { phone: '"title" "shot" "start"', desktop: '"title shot" "start shot"' }

function Landing({ onOpenInfo, signedIn = false, onContinue, deleted = false, translate }) {
  const isMobile = useIsMobile()
  /* Read once: the ad that sent this visitor (adLanding.js) and the browser
   * they are in. Reddit's app opens links in its own WebView, where Google
   * sign-in cannot work, and an iPhone has no use for a Google Play badge.
   * Website only for the first: the Android app is a WebView too, but its
   * Google sign-in goes through the platform SDK (native.js) and works. */
  const [ad] = useState(() => landingAd(readAttribution()?.content))
  const [agent] = useState(() => classifyUserAgent(typeof navigator === 'undefined' ? '' : navigator.userAgent))
  const locale = getLocale()
  const hero = heroImage(ad, locale)
  /* The landing's visit record (landingVisit.js): how many people a link sent
   * here and what they did, with no consent needed because nothing is stored
   * on the device. Only for a visitor who hasn't started yet, on the website. */
  useEffect(() => {
    if (signedIn || platform !== 'web') return undefined
    startLandingVisit({ version: LANDING_VERSION })
    noteLanding(LANDING_VERSION)
    return () => pauseLandingVisit()
  }, [signedIn])
  /* Whether anyone reads past the first screen: half of the first feature's
   * picture in view is the visit's "more" act. The list's heading would not
   * do: it already shows at the bottom of a phone's first screen. A no-op
   * until landingVisit.js knows the act (it ignores any it doesn't), so this
   * can ship before the rules do. */
  const more = useRef(null)
  useEffect(() => {
    if (signedIn || platform !== 'web' || !more.current || typeof IntersectionObserver === 'undefined') return undefined
    const seen = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return
      noteLandingAct('more')
      seen.disconnect()
    }, { threshold: 0.5 })
    seen.observe(more.current)
    return () => seen.disconnect()
  }, [signedIn])
  const column = { boxSizing: 'border-box', margin: '0 auto', maxWidth: 1040, padding: `0 clamp(16px, 5vw, 72px)` }
  return (
    <main style={{ minHeight: '100vh', background: C.bg, color: C.ink, fontFamily: F.sans, paddingBottom: 'calc(40px + var(--safe-bottom, 0px))' }}>
      <nav style={{ alignItems: 'center', borderBottom: `1px solid ${C.faint}`, display: 'flex', justifyContent: 'space-between', padding: '13px clamp(16px, 5vw, 72px)', paddingTop: 'calc(13px + var(--safe-top, 0px))' }}>
        <span style={{ ...T.title, alignItems: 'center', display: 'flex', gap: 8 }}><img src="/favicon.svg" alt="" style={{ height: 25, width: 25 }} />TowardsGM</span>
        {signedIn ? (
          <button onClick={onContinue} style={{ ...authLink, whiteSpace: 'nowrap' }}>{translate('app.continueTraining')}</button>
        ) : isMobile ? null : (
          /* Not on a phone: the headline under it already says it, and in
             Spanish the two words wrap into the name. */
          <span style={{ ...T.label, color: C.sub }}>{translate('app.landingTagline')}</span>
        )}
      </nav>
      {/* The first screen: the promise of the ad that was tapped, its own
          picture, and one button. A phone stacks them in that order so the
          picture sits between the promise and the button; a desktop puts the
          picture beside them. */}
      <section style={{ ...column, paddingTop: isMobile ? 18 : 56 }}>
        <div
          style={{
            alignItems: 'center',
            columnGap: 48,
            display: 'grid',
            gridTemplateAreas: isMobile ? HERO_AREAS.phone : HERO_AREAS.desktop,
            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1.1fr)',
            rowGap: 14,
          }}
        >
          <h1 style={{ ...T.display, alignSelf: 'end', fontSize: 'clamp(27px, 4.6vw, 48px)', gridArea: 'title', margin: 0 }}>{translate(`app.landingAds.${ad}.title`)}</h1>
          <div style={{ gridArea: 'shot' }}>
            <LandingShot shot={hero} hero isMobile={isMobile} />
          </div>
          <div style={{ alignSelf: 'start', gridArea: 'start' }}>
            <p style={{ ...T.body, color: C.sub, margin: '0 0 14px', maxWidth: 430 }}>{translate('app.landingFromGames')}</p>
            {/* Deletion lands here, on a page loaded from scratch — see the note
                in DeleteAccount.jsx. This card is the only proof the user gets
                that it worked, so it says what happened in past tense. */}
            {deleted ? (
              <div style={{ ...surface('accent'), boxSizing: 'border-box', marginBottom: 18, padding: 18, width: 'min(100%, 380px)' }}>
                <h2 style={{ ...T.title, margin: '0 0 4px' }}>{translate('app.deleted')}</h2>
                <div style={{ ...T.caption, color: C.sub }}>
                  Your progress, games and account are gone from this device and from our servers. Thanks for
                  training with us — you’re welcome back any time, from scratch.
                </div>
              </div>
            ) : null}
            {signedIn ? (
              <div style={{ ...surface(), boxSizing: 'border-box', padding: 18, width: 'min(100%, 380px)' }}>
                <div style={{ ...T.title, marginBottom: 4 }}>{translate('app.welcomeBack')}</div>
                <div style={{ ...T.caption, color: C.sub, marginBottom: 15 }}>{translate('app.welcomeBackNote')}</div>
                <button onClick={onContinue} style={{ ...authPrimary, width: '100%' }}>{translate('app.continueToTrainer')}</button>
              </div>
            ) : (
              <div style={{ width: 'min(100%, 380px)' }}>
                <AuthPanel showGuest foldAccount hideGoogle={platform === 'web' && Boolean(agent.app)} surface="landing" />
              </div>
            )}
            <LandingPlayBadge signedIn={signedIn} ios={agent.os === 'ios'} translate={translate} />
          </div>
        </div>
      </section>
      {/* How it works, for whoever stays past the first screen: the composite's
          three steps (reads your games, finds your leaks, trains the fix), a
          feature each, every one but the ad already shown. A desktop
          alternates picture and words row by row; a phone stacks them. */}
      <section style={{ ...column, paddingTop: isMobile ? 48 : 96 }}>
        <h2 style={{ ...T.display, margin: isMobile ? '0 0 28px' : '0 0 56px', textAlign: isMobile ? 'left' : 'center' }}>{translate('app.landingMoreKicker')}</h2>
        <div style={{ display: 'grid', rowGap: isMobile ? 56 : 96 }}>
          {moreFeatures(ad).map((feature, index, all) => {
            const flip = !isMobile && index % 2 === 1
            /* The step heads the first feature that belongs to it, numbered
               like the composite's 1-2-3; the next one in the same step
               carries on under it. */
            const step = STEPS.indexOf(feature.step) + 1
            const opens = index === 0 || all[index - 1].step !== feature.step
            return (
              <article key={feature.copy} style={{ alignItems: 'center', columnGap: 64, display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)', rowGap: 20 }}>
                <div style={{ order: flip ? 2 : 1 }}>
                  {opens ? (
                    <div style={{ ...T.label, alignItems: 'center', color: C.brass, display: 'flex', gap: 8, marginBottom: 10 }}>
                      <span aria-hidden="true" style={{ alignItems: 'center', background: C.brass, borderRadius: R.pill, color: C.card, display: 'inline-flex', height: 24, justifyContent: 'center', width: 24 }}>{step}</span>
                      {translate(`app.landingSteps.${feature.step}`)}
                    </div>
                  ) : null}
                  <h2 style={{ ...(isMobile ? T.title : T.display), margin: '0 0 8px' }}>{translate(`app.landingAds.${feature.copy}.title`)}</h2>
                  <p style={{ ...T.body, color: C.sub, margin: 0, maxWidth: 440 }}>{translate(`app.landingAds.${feature.copy}.body`)}</p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', order: flip ? 1 : 2 }}>
                  <LandingShot shot={landingImage(feature.picture, locale)} imgRef={index === 0 ? more : undefined} isMobile={isMobile} />
                </div>
              </article>
            )
          })}
        </div>
        {/* The close: on a desktop it sits under the middle of the two
            columns above it, not under the left one. */}
        <div style={isMobile ? undefined : { alignItems: 'center', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
        {signedIn ? null : (
          <div style={{ ...surface('accent'), boxSizing: 'border-box', marginTop: isMobile ? 36 : 56, padding: 18, textAlign: 'left', width: 'min(100%, 420px)' }}>
            <h2 style={{ ...T.title, margin: '0 0 4px' }}>{translate('app.landingEndTitle')}</h2>
            <p style={{ ...T.caption, color: C.sub, margin: '0 0 14px' }}>{translate('app.beginNote')}</p>
            <AuthPanel showGuest guestOnly surface="landing" />
          </div>
        )}
        <div style={{ maxWidth: 480 }}>
          {/* Merchant-of-record verification (Creem) needs pricing stated in
              plain sight on the public site, not just at checkout — the Pro
              paywall itself deliberately shows no numbers (see ProModal.jsx)
              so this is the one place they're hardcoded. If the live Creem
              price ever changes, this copy needs a manual update alongside
              it; there is no dynamic link between the two.
              Website only. The store shells are consumption-only (see
              WEB_CHECKOUT in creemCheckout.js): a web price on the first
              screen of the Android app is an advert for a way to pay that the
              app may not offer, and Creem verifies the public site, not the
              shell. */}
          {WEB_CHECKOUT ? (
            <div data-testid="landing-pricing" style={{ marginTop: 22, maxWidth: 430 }}>
              <div style={{ ...T.label, color: C.brass, marginBottom: 6 }}>{translate('app.pricingKicker')}</div>
              <p style={{ ...T.small, color: C.sub, margin: 0 }}>{translate('app.pricingBody')}</p>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 14, marginTop: 18, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'center' }}>
            <button onClick={() => onOpenInfo('contact')} style={authLink}>{translate('app.contact')}</button>
            <button onClick={() => onOpenInfo('privacy')} style={authLink}>{translate('app.privacy')}</button>
            <button onClick={() => onOpenInfo('terms')} style={authLink}>{translate('app.terms')}</button>
            {/* i18n-ignore — opens the English-only Legal notice (see InfoPage
                below); same reasoning as the support email that follows it. */}
            <button onClick={() => onOpenInfo('legal')} style={authLink}>Legal</button>
            <button onClick={() => onOpenInfo('licenses')} style={authLink}>{translate('app.licenses')}</button>
            {/* i18n-ignore — a support address, not translatable copy. */}
            <a href="mailto:support@towardsgm.com" style={authLink}>support@towardsgm.com</a>
          </div>
        </div>
        </div>
      </section>
    </main>
  )
}

function InfoPage({ page, user, onBack, onOpenDeleteAccount, onOpenPage }) {
  const translate = useT()
  /* The stored value stays ENGLISH (i18n-ignore). It is what lands in the
   * feedback collection and what the analytics event is derived from, so a
   * Spanish reader picking "Informe de error" must still file a "Bug report"
   * — only the label the option WEARS is translated. */
  const [topic, setTopic] = useState('Product feedback')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState('')
  /* Tracked rather than sniffed from the text. The colour used to come from
   * status.startsWith('Thanks'), which stops being true the moment the
   * message is translated. */
  const [ok, setOk] = useState(false)
  const [sending, setSending] = useState(false)
  const isPrivacy = page === 'privacy'
  const isTerms = page === 'terms'
  const isLegal = page === 'legal'

  const sendFeedback = async (event) => {
    event.preventDefault()
    if (!message.trim()) return
    setSending(true)
    setStatus('')
    setOk(false)
    if (!user) {
      const session = await continueAnonymously()
      if (session.error) {
        setSending(false)
        setStatus(session.error)
        return
      }
    }
    const result = await submitFeedback({ topic, email, message })
    setSending(false)
    if (result.error) {
      setStatus(result.error)
      return
    }
    trackEvent('feedback_submitted', { topic: topic.toLowerCase().replaceAll(' ', '_'), signed_in: Boolean(user && !user.isAnonymous) })
    setMessage('')
    setEmail('')
    setOk(true)
    setStatus(translate('app.contactThanks'))
  }

  return (
    <main style={{ minHeight: '100vh', background: C.bg, color: C.ink, fontFamily: F.sans, padding: '22px 14px 56px', paddingTop: 'calc(22px + var(--safe-top, 0px))' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <button onClick={onBack} style={{ ...authLink, marginBottom: 18, padding: 0 }}>{translate('app.backToApp')}</button>
        {/* i18n-ignore — the privacy policy stays English by Pablo's
            instruction (2026-08-18): it's a translation liability, and the
            Play data-safety declaration derives from it 1:1, so a Spanish
            paraphrase that drifts from it is a store problem as well as a
            legal one. Terms of Service is different: Creem's merchant
            verification (2026-08-20) needs it readable in Spanish too, and
            nothing else derives from it 1:1, so TERMS_EN/TERMS_ES below are
            two independently-authored copies (not machine-translated at
            build time) kept in sync by hand. The Legal notice (2026-08-22)
            follows the Privacy rule instead: it's an identity/NIF block,
            LSSI-CE requires one of its sentences verbatim in Spanish
            regardless of site language, and Pablo's identity doesn't need a
            second authored version to drift from the first. Its nav label is
            hardcoded for the same reason the support email is — see Landing.
            The chrome AROUND all three — the back button, the kicker, the
            Contact form on the same page — is translated. */}
        {isPrivacy ? (
          <>
            <div style={{ ...T.label, color: C.brass, marginBottom: 8 }}>PRIVACY</div>
            <h1 style={{ ...T.display, margin: '0 0 12px' }}>Privacy policy</h1>
            <div style={infoCard}>
              <p style={infoCopy}>TowardsGM stores the progress, preferences, and game analyses you choose to create so your personal trainer can work across sessions and devices.</p>
              <h2 style={infoHeading}>What we store</h2>
              <p style={infoCopy}>Your account identifier, learning progress, settings, Ledger preferences, and imported or fetched game analysis are stored locally and, when you use an account, in Firebase services for your account only. Your game data is used to build your personal learning path and mistake drills. If you sign in with Google, we receive your name, email address and profile picture from Google to identify your account.</p>
              <h2 style={infoHeading}>Analytics</h2>
              <p style={infoCopy}>With your opt-in, we use Google Analytics to measure aggregate product activity such as starting a session, importing games, opening a course, finishing a drill, and sending feedback. We do not send usernames, email addresses, Firebase user IDs, PGN, move lists, or feedback text to Analytics. You can change this choice in Settings.</p>
              <h2 style={infoHeading}>Visits to the website</h2>
              <p style={infoCopy}>When you open the website's first page, it records one visit so we can count how many people a link or an ad brought and whether they started training: the link's campaign tags, your device type (phone, tablet or desktop), its system, your browser's language, how long the page was visible, and which start button you pressed. Nothing is stored on your device for this and no identifier is created, so two visits from you are two unrelated records, never linked to an account. It needs no Analytics consent for that reason, and the records are deleted after 90 days.</p>
              <h2 style={infoHeading}>Error monitoring</h2>
              <p style={infoCopy}>When something in the app breaks, an error report is sent to Vigilia, our error-monitoring provider, so we can find and fix it. A report contains the error itself, the page it happened on, your browser or device type, and the last few actions before it: which buttons were pressed, which pages were opened and which network requests were made. It is tied to a random identifier that lasts one session, not to your account, and email addresses and sign-in tokens are removed from the text before it leaves your device. Error monitoring is part of keeping the app working, so it is not covered by the Analytics choice above.</p>
              <h2 style={infoHeading}>Feedback</h2>
              <p style={infoCopy}>Messages sent through Contact are stored with your account so we can review them. An email address is optional and used only to reply to you.</p>
              <h2 style={infoHeading}>Email</h2>
              <p style={infoCopy}>We email you only if you ask us to. If you turn on the weekly recap in Settings, it is sent to your account's address through Resend, our email provider, and every message carries an unsubscribe link.</p>
              <h2 style={infoHeading}>Payments</h2>
              <p style={infoCopy}>TowardsGM Pro is sold on our website through Creem, our payment processor and merchant of record. Creem handles your payment details; we never see them. We store that your account has Pro, together with Creem's reference for the customer and the subscription. When you tap a Pro plan, or ask to be told when Pro is available in the app, we also record which one and where you were, against your account, so we can see how the offer is working and write to you about it.</p>
              <h2 style={infoHeading}>Your choices</h2>
              <p style={infoCopy}>You can use the app anonymously. Sign out ends the current local session but does not erase data already synced to an account.</p>
              <h2 style={infoHeading}>Deleting your account and data</h2>
              <p style={infoCopy}>You can delete everything yourself, at any time, from Settings → Account data — or from <button onClick={() => onOpenDeleteAccount()} style={{ ...authLink, padding: 0 }}>towardsgm.com/delete-account</button> if you no longer have the app installed. It erases your account, your learning progress, your imported games and analyses, your preferences and any feedback you sent, from this device and from our servers. It is immediate and permanent: we keep no backup to restore from. Use Contact if you would rather we did it for you, or to ask a privacy question.</p>
              <p style={infoCopy}>Last updated: October 13, 2026.</p>
              <p style={{ ...infoCopy, marginBottom: 0 }}>See also the <button onClick={() => onOpenPage('terms')} style={{ ...authLink, padding: 0 }}>Terms of Service</button> and the <button onClick={() => onOpenPage('legal')} style={{ ...authLink, padding: 0 }}>Legal notice</button>.</p>
            </div>
          </>
        ) : isLegal ? (
          <>
            <div style={{ ...T.label, color: C.brass, marginBottom: 8 }}>LEGAL</div>
            <h1 style={{ ...T.display, margin: '0 0 12px' }}>Legal notice</h1>
            <div style={infoCard}>
              <p style={infoCopy}>Aviso Legal. This page covers who operates TowardsGM, as required by Spanish law.</p>
              <h2 style={infoHeading}>Site operator</h2>
              <p style={infoCopy}>En cumplimiento del artículo 10 de la Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se facilitan los siguientes datos:</p>
              <p style={infoCopy}>
                <strong>Name:</strong> Pablo Matías Pérez Piskunow<br />
                <strong>Address:</strong> Carrer de Gorina 4, piso 1 puerta 1, CP 08172, Sant Cugat del Vallès, Barcelona, España<br />
                <strong>NIF:</strong> 60275227R<br />
                <strong>Email:</strong> <a href="mailto:support@towardsgm.com" style={{ color: 'inherit' }}>support@towardsgm.com</a>
              </p>
              <p style={infoCopy}>TowardsGM is operated by Pablo Matías Pérez Piskunow, trading as a sole trader (autónomo) registered in Spain.</p>
              <h2 style={infoHeading}>Prices &amp; taxes</h2>
              <p style={{ ...infoCopy, marginBottom: 0 }}>Prices shown on this site (Pro: €5/month or €36/year) are final prices, VAT included where applicable — you never pay more than the price shown, regardless of where you're buying from. Our payment processor and merchant of record, Creem, is responsible for calculating, collecting and remitting any VAT or local sales tax due.</p>
            </div>
          </>
        ) : isTerms ? (
          <>
            {(() => {
              const terms = getLocale() === 'es' ? TERMS_ES : TERMS_EN
              return (
                <>
                  <div style={{ ...T.label, color: C.brass, marginBottom: 8 }}>{terms.kicker}</div>
                  <h1 style={{ ...T.display, margin: '0 0 12px' }}>{terms.title}</h1>
                  <div style={infoCard}>
                    {terms.sections.map((section) => (
                      <div key={section.h}>
                        <h2 style={infoHeading}>{section.h}</h2>
                        {section.p.map((paragraph, i) => <p key={i} style={infoCopy}>{paragraph}</p>)}
                      </div>
                    ))}
                    <p style={infoCopy}>{terms.updated}</p>
                    <p style={{ ...infoCopy, marginBottom: 0 }}>{terms.seeAlsoPre}<button onClick={() => onOpenPage('privacy')} style={{ ...authLink, padding: 0 }}>{terms.privacyLink}</button>{terms.seeAlsoMid}<button onClick={() => onOpenPage('legal')} style={{ ...authLink, padding: 0 }}>{terms.legalLink}</button>{terms.seeAlsoPost}</p>
                  </div>
                </>
              )
            })()}
          </>
        ) : (
          <>
            <div style={{ ...T.label, color: C.brass, marginBottom: 8 }}>{translate('app.contactKicker')}</div>
            <h1 style={{ ...T.display, margin: '0 0 8px' }}>{translate('app.contactTitle')}</h1>
            <p style={{ ...T.body, color: C.sub, margin: '0 0 18px' }}>{translate('app.contactBlurb')}</p>
            <form onSubmit={sendFeedback} style={{ ...infoCard, display: 'grid', gap: 12 }}>
              <label style={formLabel}>{translate('app.contactTopic')}<select value={topic} onChange={(event) => setTopic(event.target.value)} style={authInput}><option value="Product feedback">{translate('app.topics.product')}</option><option value="Bug report">{translate('app.topics.bug')}</option><option value="Opening content">{translate('app.topics.content')}</option><option value="Privacy request">{translate('app.topics.privacy')}</option></select></label>
              <label style={formLabel}>{translate('app.contactEmail')}<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" style={authInput} /></label>
              <label style={formLabel}>{translate('app.contactMessage')}<textarea value={message} onChange={(event) => setMessage(event.target.value)} required maxLength="4000" rows="6" style={{ ...authInput, resize: 'vertical' }} /></label>
              {status ? <div style={{ ...T.caption, color: ok ? TONES.success.ink : TONES.danger.ink }}>{status}</div> : null}
              <button disabled={sending || !message.trim()} type="submit" style={authPrimary}>{sending ? translate('app.contactSending') : translate('app.contactSend')}</button>
            </form>
          </>
        )}
      </div>
    </main>
  )
}

const infoCard = { ...surface(), padding: '18px 20px' }
const infoHeading = { ...T.title, fontSize: 19, margin: '20px 0 5px' }
const infoCopy = { ...T.small, color: C.sub, lineHeight: 1.6, margin: '0 0 10px' }
const formLabel = { ...T.caption, color: C.sub, display: 'grid', gap: 5 }

/* i18n-ignore — Terms of Service, hand-translated per the note above `isTerms`
 * in InfoPage: TERMS_EN and TERMS_ES are two independently-authored copies,
 * not machine-translated at build time, kept in sync by hand when either
 * changes. Unlike the privacy policy, nothing else in the app or the Play
 * listing derives from this 1:1, and Creem's merchant verification
 * (2026-08-20) needs it readable in Spanish, so it gets a real translation
 * rather than staying English-only. */
const TERMS_EN = {
  kicker: 'TERMS',
  title: 'Terms of Service',
  sections: [
    { h: '1. The service', p: ['TowardsGM is a chess opening-training web app with two areas: Ledger, which mines your own game history for opening statistics, and Learn, a curriculum of courses, drills and spaced repetition. By creating an account or otherwise using the Service you agree to these Terms.'] },
    { h: '2. Accounts', p: ['You can use TowardsGM without an account — progress then stays on this device only — or sign in to sync your progress across devices. You are responsible for keeping your account credentials secure and for all activity under your account. Do not share your account with others.'] },
    { h: '3. Subscription plans, billing & cancellation', p: ['The Free plan gives access to the Ledger and a substantial part of the Learn curriculum. Pro (€5/month or €36/year) unlocks the deeper parts of the catalog and your full mistakes deck. Pro is billed by Creem, our payment processor and merchant of record — Creem, not TowardsGM, handles your payment details and any applicable tax.', 'You can cancel anytime from Settings → Manage subscription; access continues until the end of the period you already paid for. Fees already charged are non-refundable except where required by law. We may change prices for future billing periods with reasonable notice.'] },
    { h: '4. Your content', p: ['Games you import or connect, and any analysis derived from them, remain yours. We use them only to build your personal learning path and mistake drills, and do not sell them or share them with third parties beyond what the Privacy policy describes.'] },
    { h: '5. Availability & disclaimer', p: ['TowardsGM is provided "as is", without warranties of any kind. We aim for high availability but do not guarantee uninterrupted service or that every analysis is error-free. TowardsGM is a training aid, not a substitute for your own judgement over the board.'] },
    { h: '6. Liability', p: ['To the maximum extent permitted by law, our total liability arising out of these Terms is limited to the amount you paid us in the 12 months before the claim. We are not liable for indirect or consequential damages.'] },
    { h: '7. Termination', p: ['You may delete your account and all associated data at any time from Settings → Account data, or from towardsgm.com/delete-account if you no longer have the app installed — see the Privacy policy for what that erases. We may suspend or terminate accounts that abuse the Service or violate these Terms.'] },
    { h: '8. Changes to these terms', p: ['We may update these Terms from time to time; the "Last updated" date below reflects the latest version. Continued use of the Service after a change constitutes acceptance of the updated Terms.'] },
    { h: '9. Governing law & contact', p: ['These Terms are governed by the laws applicable to the operation of the Service, without regard to conflict-of-laws principles. Questions about these Terms can be sent to support@towardsgm.com.'] },
  ],
  updated: 'Last updated: August 20, 2026.',
  seeAlsoPre: 'See also the ',
  privacyLink: 'Privacy policy',
  seeAlsoMid: ' and the ',
  legalLink: 'Legal notice',
  seeAlsoPost: '.',
}
const TERMS_ES = {
  kicker: 'TÉRMINOS',
  title: 'Términos de servicio',
  sections: [
    { h: '1. El servicio', p: ['TowardsGM es una aplicación web de entrenamiento de aperturas de ajedrez con dos áreas: Ledger, que analiza tu propio historial de partidas para extraer estadísticas de apertura, y Learn, un plan de estudio con cursos, ejercicios y repetición espaciada. Al crear una cuenta o usar el Servicio de cualquier otra forma, aceptas estos Términos.'] },
    { h: '2. Cuentas', p: ['Puedes usar TowardsGM sin una cuenta — en ese caso tu progreso se guarda solo en este dispositivo — o iniciar sesión para sincronizar tu progreso entre dispositivos. Eres responsable de mantener seguras tus credenciales y de toda la actividad de tu cuenta. No compartas tu cuenta con otras personas.'] },
    { h: '3. Planes, facturación y cancelación', p: ['El plan Gratis da acceso al Ledger y a gran parte del plan de estudio de Learn. Pro (5€/mes o 36€/año) desbloquea las partes más profundas del catálogo y tu mazo completo de errores. Pro se factura a través de Creem, nuestro procesador de pagos y comerciante registrado (merchant of record): es Creem, no TowardsGM, quien gestiona tus datos de pago y cualquier impuesto aplicable.', 'Puedes cancelar en cualquier momento desde Ajustes → Gestionar suscripción; el acceso continúa hasta el final del periodo ya pagado. Los importes ya cobrados no son reembolsables salvo que la ley lo exija. Podemos cambiar los precios para futuros periodos de facturación con un aviso razonable.'] },
    { h: '4. Tu contenido', p: ['Las partidas que importas o conectas, y cualquier análisis derivado de ellas, siguen siendo tuyas. Las usamos únicamente para construir tu plan de aprendizaje personal y tus ejercicios de errores, y no las vendemos ni las compartimos con terceros más allá de lo que describe la Política de privacidad.'] },
    { h: '5. Disponibilidad y renuncia de garantías', p: ['TowardsGM se ofrece "tal cual", sin garantías de ningún tipo. Buscamos una alta disponibilidad, pero no garantizamos un servicio ininterrumpido ni que cada análisis esté libre de errores. TowardsGM es una ayuda de entrenamiento, no un sustituto de tu propio criterio ante el tablero.'] },
    { h: '6. Responsabilidad', p: ['En la máxima medida permitida por la ley, nuestra responsabilidad total derivada de estos Términos se limita al importe que nos hayas pagado en los 12 meses anteriores a la reclamación. No somos responsables de daños indirectos o consecuentes.'] },
    { h: '7. Terminación', p: ['Puedes eliminar tu cuenta y todos los datos asociados en cualquier momento desde Ajustes → Datos de la cuenta, o desde towardsgm.com/delete-account si ya no tienes la aplicación instalada — consulta la Política de privacidad para ver qué se borra. Podemos suspender o cancelar cuentas que abusen del Servicio o incumplan estos Términos.'] },
    { h: '8. Cambios en estos términos', p: ['Podemos actualizar estos Términos de vez en cuando; la fecha de "Última actualización" más abajo refleja la versión vigente. El uso continuado del Servicio tras un cambio implica la aceptación de los Términos actualizados.'] },
    { h: '9. Ley aplicable y contacto', p: ['Estos Términos se rigen por las leyes aplicables a la operación del Servicio, sin dar efecto a sus disposiciones sobre conflicto de leyes. Puedes enviar preguntas sobre estos Términos a support@towardsgm.com.'] },
  ],
  updated: 'Última actualización: 20 de agosto de 2026.',
  seeAlsoPre: 'Consulta también la ',
  privacyLink: 'Política de privacidad',
  seeAlsoMid: ' y el ',
  legalLink: 'Aviso legal',
  seeAlsoPost: '.',
}

/* The app's ONE settings surface: who you are, the active tab's own settings,
 * then appearance and privacy. Board/drill prefs persist in the learn settings
 * blob (already synced to the account via learn/sync.js); the Ledger portals
 * its own sections into SETTINGS_SLOT_ID rather than having its prefs lifted
 * up here — see the note on that constant in Sheet.jsx.
 *
 * This used to be two nav buttons, a gear and an account avatar with its own
 * dropdown, plus a third gear inside the Ledger. Three entry points for
 * "change something about my setup" is two too many. */
function SettingsModal({ onClose, user, onOpenAuth, onOpenLicenses, mobile }) {
  const [settings, setSettings] = useState(() => loadLearn().settings)
  const [analyticsAllowed, setAnalyticsAllowed] = useState(() => analyticsConsent() === 'granted')
  const [helpPage, setHelpPage] = useState(null) // K3a — which Help topic is open, if any
  const translate = useT()
  const [proUser, setProUser] = useState(isProUser())
  const [billingAccount, setBillingAccount] = useState(hasBillingAccount())
  useEffect(() => subscribePro((pro) => { setProUser(pro); setBillingAccount(hasBillingAccount()) }), [])
  const [portalState, setPortalState] = useState('idle') // idle | loading | error
  const manageBilling = async () => {
    setPortalState('loading')
    try {
      await openBillingPortal()
      // Success navigates the browser away; no need to reset state here.
    } catch {
      setPortalState('error')
    }
  }

  const update = (patch) => {
    const next = { ...settings, ...patch }
    setSettings(next)
    const state = loadLearn()
    state.settings = { ...state.settings, ...patch }
    saveLearn(state)
    window.dispatchEvent(new Event('towardsgm-settings-changed'))
  }

  const radio = (active) => ({
    ...T.small,
    background: active ? TONES.accent.ground : 'transparent',
    border: `1.5px solid ${active ? C.brass : C.edge}`,
    borderRadius: R.md,
    color: active ? C.brass : C.sub,
    cursor: 'pointer',
    fontWeight: 700,
    minHeight: 44,
    padding: '10px 12px',
    textAlign: 'left',
  })
  const check = { ...T.small, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 2px', color: C.ink, cursor: 'pointer' }
  const guest = !user || user.isAnonymous
  const initial = (user?.displayName || user?.email || '?').slice(0, 1).toUpperCase()

  if (helpPage === 'notation') {
    return <NotationKey translate={translate} onClose={() => setHelpPage(null)} mobile={mobile} />
  }
  if (helpPage === 'drills') {
    return <DrillHelp translate={translate} onClose={() => setHelpPage(null)} mobile={mobile} />
  }

  return (
    <Sheet title={translate('app.settings')} onClose={onClose} mobile={mobile}>
      {/* Account first: it answers "am I even signed in", which is the
          question behind most of the others. */}
      <div style={{ alignItems: 'center', display: 'flex', gap: 12, marginTop: 10 }}>
        <span
          style={{
            ...T.data,
            alignItems: 'center',
            background: TONES.accent.ground,
            border: `1px solid ${TONES.accent.edge}`,
            borderRadius: '50%',
            color: C.brass,
            display: 'grid',
            flexShrink: 0,
            fontSize: 16,
            height: 40,
            justifyItems: 'center',
            overflow: 'hidden',
            placeItems: 'center',
            width: 40,
          }}
        >
          {user?.photoURL ? (
            <img src={user.photoURL} alt="" referrerPolicy="no-referrer" style={{ height: '100%', objectFit: 'cover', width: '100%' }} />
          ) : guest ? '♟' : initial}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...T.small, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {guest ? translate('app.guest') : user.displayName || user.email}
          </div>
          <div style={{ ...T.caption, color: C.sub, marginTop: 2 }}>
            {guest ? translate('app.guestNote') : translate('app.signedNote')}
          </div>
        </div>
        {guest ? (
          <button onClick={() => { onClose(); onOpenAuth() }} style={{ ...authPrimary, flexShrink: 0, whiteSpace: 'nowrap' }}>{translate('app.signIn')}</button>
        ) : (
          <button
            onClick={() => { onClose(); signOutUser() }}
            style={{ ...chip(false), flexShrink: 0 }}
          >
            {translate('app.signOut')}
          </button>
        )}
      </div>

      {/* Creem's own billing portal (functions/index.js#creemBillingPortal),
          not a page of our own — cancellation, payment methods and invoices
          all live there already, and a second UI for them here would just be
          a worse copy that drifts from what Creem actually shows. */}
      {!guest && proUser && !billingAccount ? (
        // A comp: Pro with no Creem customer behind it, so no portal to open
        // (creemBillingPortal would answer failed-precondition — Vigilia
        // XJFXhRtjq3Jt3P9pS0Ia). Say what the plan is instead of a dead button.
        <div style={{ marginTop: 10, ...T.small, color: C.sub }}>{translate('pro.complimentary')}</div>
      ) : null}
      {!guest && proUser && billingAccount && !WEB_CHECKOUT ? (
        // A store shell may say that a subscription exists and may not link to
        // where it is billed: Creem's portal takes card details, which makes it
        // "a page that leads to another payment method" in Play's own wording.
        // Plain text, no link — the subscriber already has Creem's receipts.
        <div data-testid="pro-managed-elsewhere" style={{ marginTop: 10, ...T.small, color: C.sub }}>{translate('pro.managedElsewhere')}</div>
      ) : null}
      {!guest && proUser && billingAccount && WEB_CHECKOUT ? (
        <div style={{ marginTop: 10 }}>
          <button
            onClick={manageBilling}
            disabled={portalState === 'loading'}
            style={{ ...GHOST, cursor: portalState === 'loading' ? 'default' : 'pointer', marginLeft: -4 }}
          >
            {portalState === 'loading' ? translate('pro.openingPortal') : translate('pro.manageSubscription')}
          </button>
          {portalState === 'error' ? <div style={{ ...authError, marginTop: 6 }}>{translate('pro.manageSubscriptionError')}</div> : null}
        </div>
      ) : null}

      {/* The active tab fills this. Rendered unconditionally rather than gated
          on which tab is showing: only the mounted tab can portal into it, so
          the gate was saying twice what mounting already says — and it was
          saying it wrong, since it named the Ledger specifically and therefore
          left the Learn tab with nowhere to put a section of its own. That is
          why "Redo setup" had no home for as long as it didn't exist. */}
      <div id={SETTINGS_SLOT_ID} />

      <NotifySettings user={user} />

      <div style={sheetSection}>{translate('app.board')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
        {Object.entries(BOARD_PRESETS).map(([key, preset]) => (
          <button key={key} onClick={() => update({ boardTheme: key })} style={radio(settings.boardTheme === key)}>
            <span style={{ display: 'inline-flex', marginRight: 8, verticalAlign: 'middle' }}>
              {[preset.light, preset.dark, preset.light, preset.dark].map((c, i) => (
                <span key={i} style={{ width: 10, height: 10, background: c, display: 'inline-block' }} />
              ))}
            </span>
            {/* `label` is a key: BOARD_PRESETS is a module constant, so it
                cannot hold a resolved string. */}
            {translate(preset.label)}
          </button>
        ))}
      </div>

      {/* M1b · the escape for people who already read SAN fluently. Lives in
          the board section because it is about how the board's own moves are
          written, and because CLAUDE.md's one-settings-surface rule means it
          does not get a section of its own. */}
      <label style={{ ...check, marginTop: 10 }}>
        <input
          type="checkbox"
          checked={settings.figurineNotation !== false}
          onChange={(e) => update({ figurineNotation: e.target.checked })}
          style={{ accentColor: C.brass, height: 17, width: 17 }}
        />
        {translate('settings.figurine')}
      </label>

      <div style={sheetSection}>{translate('app.drills')}</div>
      <div style={{ marginTop: 6 }}>
        {[
          ['sound', 'app.sound'],
          ['showLegalMoves', 'app.legalMoves'],
        ].map(([key, label]) => (
          <label key={key} style={check}>
            <input
              type="checkbox"
              checked={!!settings[key]}
              onChange={(e) => update({ [key]: e.target.checked })}
              style={{ accentColor: C.brass, height: 17, width: 17 }}
            />
            {translate(label)}
          </label>
        ))}
      </div>

      {/* K3a · everything a first-encounter explainer says fires once and
          never returns, which is right — and leaves whoever dismissed it with
          no way back. This is that way back, inside the ONE settings surface
          rather than as a second one (CLAUDE.md). "Replay the setup" already
          lives in the Learn tab's own portalled section as A9's Redo setup. */}
      {/* O2 · the locale switcher. `locale` has existed since O1 and nothing
          set it, so Spanish was reachable only by editing localStorage. Lives
          in the appearance half of the one settings surface rather than
          getting a section of its own — it is a display preference, and
          CLAUDE.md forbids a second settings surface. */}
      <div style={sheetSection}>{translate('settings.language')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
        {SUPPORTED_LOCALES.map((code) => (
          <button key={code} onClick={() => update({ locale: code })} style={radio((settings.locale || getLocale()) === code)}>
            {LOCALE_LABEL[code]}
          </button>
        ))}
      </div>

      <div style={sheetSection}>{translate('settings.help.section')}</div>
      <div style={{ marginTop: 6 }}>
        <button
          onClick={() => setHelpPage('drills')}
          style={{ ...SECONDARY_COMPACT, marginBottom: 8, padding: '11px 12px', textAlign: 'left', width: '100%' }}
        >
          {translate('settings.help.drills')}
        </button>
        <button
          onClick={() => setHelpPage('notation')}
          style={{ ...SECONDARY_COMPACT, padding: '11px 12px', textAlign: 'left', width: '100%' }}
        >
          {translate('settings.help.notation')}
        </button>
      </div>

      <div style={sheetSection}>{translate('app.privacy')}</div>
      <div style={{ marginTop: 6 }}>
        <label style={check}>
          <input
            type="checkbox"
            checked={analyticsAllowed}
            onChange={(event) => {
              const allowed = event.target.checked
              setAnalyticsAllowed(allowed)
              setAnalyticsConsent(allowed)
              if (allowed) trackEvent('analytics_consent_granted', { surface: 'settings' })
            }}
            style={{ accentColor: C.brass, height: 17, width: 17 }}
          />
          {translate('app.analyticsOptIn')}
        </label>
      </div>

      {/* Last, and last for a reason: everything above is adjustable, this
          isn't. Lazy, so the deletion code only loads if it's opened. */}
      <Suspense fallback={null}>
        <DeleteAccountSection user={user} />
      </Suspense>

      <div style={{ ...T.caption, color: C.sub, marginTop: 14 }}>
        {translate('app.settingsFollowAccount')}
      </div>
      {/* The store builds' credits: the license texts every bundled library
          and the GPL's Stockfish require to travel with the app (Licenses.jsx). */}
      <button onClick={onOpenLicenses} data-testid="open-licenses" style={{ ...authLink, marginTop: 8, padding: 0 }}>
        {translate('app.licenses')}
      </button>
    </Sheet>
  )
}

/* One button in the nav for account AND settings: the avatar is the thing
 * people already reach for to find either. Guests get a labelled pill instead
 * of a mystery-meat icon, since "do I have an account?" is the whole question
 * at that point. */
function AccountButton({ onOpen, translate }) {
  const [{ user, ready }, setAuth] = useState({ user: null, ready: false })
  useEffect(() => subscribeAuth(setAuth), [])

  if (!ready) return <span style={{ width: 34 }} />
  const guest = !user || user.isAnonymous
  if (guest) {
    return (
      <button
        onClick={onOpen}
        title={translate('app.accountAndSettings')}
        aria-label={translate('app.account')}
        data-testid="account-settings"
        style={{ ...chip(false), ...T.label, minHeight: 32, whiteSpace: 'nowrap' }}
      >
        {translate('account.kicker')}
      </button>
    )
  }
  const initial = (user.displayName || user.email || '?').slice(0, 1).toUpperCase()
  return (
    <button
      onClick={onOpen}
      title={user.displayName || user.email || translate('app.account')}
      aria-label={translate('app.account')}
      data-testid="account-settings"
      style={{
        ...T.data,
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: `1px solid ${TONES.accent.edge}`,
        background: TONES.accent.ground,
        color: C.brass,
        cursor: 'pointer',
        padding: 0,
        overflow: 'hidden',
      }}
    >
      {user.photoURL ? (
        <img src={user.photoURL} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initial
      )}
    </button>
  )
}

class ErrorBoundary extends Component {
  state = { error: null }

  static getDerivedStateFromError(error) {
    return { error }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: '100vh',
            background: C.bg,
            color: C.ink,
            fontFamily: F.sans,
            padding: 24,
          }}
        >
          <div style={{ maxWidth: 640, margin: '0 auto' }}>
            {/* A class component, so t() directly rather than useT(). It renders
                once, on a crash — there is no locale change to re-render for. */}
            <h1 style={{ ...T.display }}>{t('app.broke')}</h1>
            <p style={{ ...T.body, color: C.sub }}>
              {this.state.error?.message || String(this.state.error)}
            </p>
            <button
              onClick={() => this.setState({ error: null })}
              style={{ ...PRIMARY, padding: '0 16px' }}
            >
              {t('ledger.tryAgain')}
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const TAB_KEY = 'towardsgm:tab'

/* X1 · the app's tabs, in bar order. The Learn tab used to be one long page
 * that held the day's session, the games, the streak, the quests and the
 * badges; the games now have a tab of their own (PlayPage.jsx). Every tab but
 * the Ledger is drawn by LearnCenter. A stored or synced value this build
 * does not know (an older or newer client's) opens Learn. */
const APP_TABS = [
  ['learn', 'app.tabLearn'],
  ['play', 'app.tabPlay'],
  // X2 · the streak, quests, rank and badges that crowded TODAY (ProgressPage.jsx).
  ['progress', 'app.tabProgress'],
  ['ledger', 'app.tabLedger'],
]
const tabFrom = (value) => (APP_TABS.some(([key]) => key === value) ? value : 'learn')

/* The standalone pages (Privacy, Contact, Delete account) answer to BOTH
 * `/#/privacy` (how the app's own links open them) and a plain `/privacy`
 * path. The plain paths exist because the Play Console's privacy-policy and
 * data-deletion fields can reject a fragment URL — and /delete-account has to
 * work for someone who has already uninstalled the app. Firebase Hosting
 * rewrites every unknown path to index.html, so reading the pathname here is
 * all it takes. */
const INFO_ROUTES = ['privacy', 'terms', 'legal', 'licenses', 'contact', 'delete-account', 'unsubscribe']
/* The analytics event each page's open fires. It used to be a ternary whose
 * last arm caught everything else, so opening the Legal notice was counted as
 * opening Delete account. */
const INFO_EVENTS = { privacy: 'privacy_opened', terms: 'terms_opened', legal: 'legal_opened', licenses: 'licenses_opened', contact: 'contact_opened', 'delete-account': 'delete_account_opened' }
const infoRouteFrom = ({ hash, pathname }) => {
  const route = hash.startsWith('#/') ? hash.slice(2) : pathname.replace(/^\/+|\/+$/g, '')
  return INFO_ROUTES.includes(route) ? route : null
}

function App() {
  const translate = useT()
  const [{ user, ready }, setAuth] = useState({ user: null, ready: false })
  const [infoPage, setInfoPage] = useState(() => infoRouteFrom(window.location))
  // Set by the delete-account flow's post-deletion reload; the flag is dropped
  // from the URL immediately so a bookmark or a share can't resurrect it.
  const [justDeleted] = useState(() => new URLSearchParams(window.location.search).has(DELETED_FLAG))
  // Mutable, not a one-shot useState: it has to fall back to false again once
  // this first ProModal closes, or every LATER open this session (the ♛ PRO
  // button, a locked line) would keep replaying the checkout-return screens.
  const [checkoutReturn, setCheckoutReturn] = useState(() => new URLSearchParams(window.location.search).get(PRO_SUCCESS_FLAG) === 'success')
  /* Arrived from the app's "send me the link" / "copy link" (proHandoff.js):
   * open the paywall straight away, annual first. Web only — a shell never
   * sells, so the flag means nothing there. Read once; the URL is cleaned below. */
  const [handoff, setHandoff] = useState(() => {
    if (!WEB_CHECKOUT) return null
    const q = new URLSearchParams(window.location.search)
    if (q.get(PRO_SUCCESS_FLAG) !== HANDOFF_OPEN) return null
    return { medium: q.get('utm_medium') || 'unknown', from: q.get('utm_source') || 'unknown', content: q.get('utm_content') || null }
  })
  const [showAnalyticsPrompt, setShowAnalyticsPrompt] = useState(() => analyticsConsent() === 'unset')
  // Default first-runs into LEARN: it works instantly (built-in courses, no
  // username needed), while the Ledger starts as an empty prompt — the worst
  // possible first screen. An explicitly chosen Ledger tab is respected.
  const [tab, setTab] = useState(() => {
    try {
      return tabFrom(localStorage.getItem(TAB_KEY))
    } catch {
      return 'learn'
    }
  })

  const pick = (t) => {
    setTab(t)
    trackEvent('tab_selected', { tab: t })
    window.scrollTo(0, 0)
    try {
      localStorage.setItem(TAB_KEY, t)
    } catch {
      /* fine */
    }
    if (user?.uid) saveRemoteAppState(user.uid, { tab: t })
  }

  const [showSettings, setShowSettings] = useState(false)
  const [showAuth, setShowAuth] = useState(false)
  const [showPro, setShowPro] = useState(checkoutReturn || Boolean(handoff))
  const [showLanding, setShowLanding] = useState(false)
  const [proUser, setProUser] = useState(isProUser())
  const [learnTarget, setLearnTarget] = useState(null)
  const isMobile = useIsMobile()
  /* A drill on a phone is a lesson, not a page: Duolingo and chess.com both
   * drop their tab bars the moment one starts, because 106px of navigation is
   * 106px of board and a stray tap on LEDGER mid-quiz is a lost session. The
   * Learn tab announces it (DrillSession mounts/unmounts) rather than App
   * reaching into its views — same boundary as `towardsgm-trained`. The drill
   * carries its own ✕, so nothing becomes unreachable. */
  const [immersive, setImmersive] = useState(false)
  useEffect(() => {
    const onImmersive = (e) => setImmersive(Boolean(e.detail))
    window.addEventListener('towardsgm-immersive', onImmersive)
    return () => window.removeEventListener('towardsgm-immersive', onImmersive)
  }, [])
  const hideChrome = isMobile && immersive && tab !== 'ledger'
  const openInfo = (page) => {
    setInfoPage(page)
    window.location.hash = `/${page}`
    if (INFO_EVENTS[page]) trackEvent(INFO_EVENTS[page], { surface: user ? 'app' : 'landing' })
    window.scrollTo(0, 0)
  }
  const closeInfo = () => {
    setInfoPage(null)
    // Back to the app root, not the current pathname: a visitor who landed on
    // /privacy directly would otherwise stay on a URL that re-opens the page.
    window.history.replaceState(null, '', '/')
  }
  const chooseAnalytics = (allowed) => {
    setAnalyticsConsent(allowed)
    setShowAnalyticsPrompt(false)
    if (allowed) trackEvent('analytics_consent_granted', { surface: 'prompt' })
  }
  useEffect(() => {
    if (justDeleted) window.history.replaceState(null, '', '/')
  }, [justDeleted])
  useEffect(() => {
    if (checkoutReturn) window.history.replaceState(null, '', window.location.pathname)
  }, [checkoutReturn])
  useEffect(() => {
    if (!handoff) return
    trackEvent('pro_handoff_landed', { medium: handoff.medium, from: handoff.from, ...(handoff.content ? { content: handoff.content } : {}) })
    window.history.replaceState(null, '', window.location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => subscribeAuth(setAuth), [])
  useEffect(() => subscribePro(setProUser), [])
  /* Pro just turned on for someone a store shell had told to buy it on the
   * website — the answer to "did the hint work". Fires from whichever device
   * the entitlement arrives on, which is the Android app itself, after the
   * resume re-check below. The flag clears so it counts once. */
  useEffect(
    () =>
      subscribePro((pro) => {
        if (!pro) return
        try {
          if (localStorage.getItem(WEB_HINT_KEY) !== '1') return
          localStorage.removeItem(WEB_HINT_KEY)
          trackEvent('pro_unlocked_after_hint', { platform })
        } catch {
          /* storage unavailable: the funnel loses a point, nothing else */
        }
      }),
    []
  )
  /* The way back from the website. Someone who subscribes in a browser and
   * returns to the Android app finds the app exactly as they left it, and the
   * entitlement is otherwise read only at launch and on sign-in — so they
   * would look free until they killed the app. Re-read it when the app comes
   * to the foreground, but only for a signed-in free account (a Pro user has
   * nothing to learn, a guest has no entitlement) and at most every 20s. */
  useEffect(() => {
    let last = 0
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || isProUser()) return
      const uid = currentUser()?.uid
      if (!uid || currentUser()?.isAnonymous) return
      if (Date.now() - last < 20_000) return
      last = Date.now()
      refreshEntitlements(uid).then((pro) => {
        if (pro) import('./learn/catalog.js').then((m) => m.refreshCatalog())
      })
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])
  useEffect(() => initLocale(), [])
  /* Entitlement + Firestore-published catalog, re-checked on every auth
   * change (waits for auth to settle so a Pro user isn't relocked by the
   * transient signed-out state during startup). */
  useEffect(() => {
    if (!ready) return
    // Dynamic import keeps the catalog data (courses + generated lines +
    // ideas) out of the main chunk; it loads alongside the Learn tab's code.
    refreshEntitlements(user?.uid).then(() => import('./learn/catalog.js').then((m) => m.refreshCatalog()))
  }, [ready, user])
  /* Reminders are re-queued rather than set once: the schedule depends on
   * which days you have already trained, so it goes stale the moment you
   * finish a session. Cheap to redo (a cancel plus up to seven local
   * notifications) and idempotent, so the honest fix is to redo it whenever
   * the answer could have changed — sign-in, returning to the app, and
   * finishing a session. See notify.js#syncReminders. */
  useEffect(() => {
    if (!ready) return
    attachNotifyPrefs(user?.uid).then(() => syncReminders())
  }, [ready, user])
  useEffect(() => {
    const resync = () => syncReminders()
    const onVisible = () => { if (document.visibilityState === 'visible') resync() }
    // Fired by the Learn tab when a session completes (LearnCenter's finalize).
    window.addEventListener('towardsgm-trained', resync)
    window.addEventListener(PREFS_CHANGED, resync)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('towardsgm-trained', resync)
      window.removeEventListener(PREFS_CHANGED, resync)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
  /* A tapped reminder was about training, so it lands on the Learn tab
   * whatever was open last. Nothing deeper: dropping someone straight into a
   * session from a notification takes the choice away from them.
   * No dependency array: `pick` is rebuilt every render, so re-subscribing is
   * the correct behaviour rather than something to memoize around. */
  useEffect(() => {
    const open = () => pick('learn')
    window.addEventListener('towardsgm-notification-opened', open)
    return () => window.removeEventListener('towardsgm-notification-opened', open)
  })
  useHardwareBack(Boolean(infoPage), closeInfo)
  useHardwareBack(showSettings, () => setShowSettings(false))
  /* The active tab renders its own settings into the panel through a portal
   * (see SETTINGS_SLOT_ID in Sheet.jsx). It can only find that host node once
   * this render has committed, so announce the change rather than making the
   * tab poll for it. */
  useEffect(() => {
    window.dispatchEvent(new Event('towardsgm-settings-panel'))
  }, [showSettings, tab])
  /* A tab can ask for the panel it contributes to — e.g. the Ledger pointing
   * at the filter that is hiding everything from its own empty state. */
  useEffect(() => {
    const open = () => setShowSettings(true)
    const close = () => setShowSettings(false)
    window.addEventListener('towardsgm-open-settings', open)
    window.addEventListener('towardsgm-close-settings', close)
    return () => {
      window.removeEventListener('towardsgm-open-settings', open)
      window.removeEventListener('towardsgm-close-settings', close)
    }
  }, [])
  useHardwareBack(showAuth, () => setShowAuth(false))
  useHardwareBack(showLanding, () => setShowLanding(false))
  useEffect(() => {
    let alive = true
    if (!user) return () => { alive = false }
    loadRemoteAppState(user.uid).then((remote) => {
      if (!alive || !remote?.tab) return
      setTab(tabFrom(remote.tab))
    })
    return () => { alive = false }
  }, [user])
  useEffect(() => {
    if (user) window.scrollTo(0, 0)
  }, [user])
  const openLearnLine = (target) => {
    setLearnTarget({ ...target, nonce: Date.now() })
    pick('learn')
  }
  const returnToLedger = () => {
    setLearnTarget(null)
    pick('ledger')
  }

  const tabStyle = (active) => ({
    ...T.label,
    background: 'none',
    border: 'none',
    borderBottom: `2px solid ${active ? C.brass : 'transparent'}`,
    color: active ? C.brass : C.sub,
    cursor: 'pointer',
    padding: '12px 4px 10px',
  })

  if (!ready) {
    return <div style={{ minHeight: '100vh', background: C.bg }} />
  }

  if (infoPage === 'unsubscribe') {
    return (
      <Suspense fallback={<TabFallback />}>
        <UnsubscribePage onBack={closeInfo} />
      </Suspense>
    )
  }

  if (infoPage === 'licenses') {
    return (
      <Suspense fallback={<TabFallback />}>
        <LicensesPage onBack={closeInfo} />
      </Suspense>
    )
  }

  if (infoPage === 'delete-account') {
    return (
      <Suspense fallback={<TabFallback />}>
        <DeleteAccountPage user={user} onBack={closeInfo} onOpenContact={() => openInfo('contact')} />
      </Suspense>
    )
  }

  if (infoPage) {
    return <><InfoPage page={infoPage} user={user} onBack={closeInfo} onOpenDeleteAccount={() => openInfo('delete-account')} onOpenPage={openInfo} />{showAnalyticsPrompt ? <AnalyticsPrompt translate={translate} onChoose={chooseAnalytics} onOpenPrivacy={() => openInfo('privacy')} /> : null}</>
  }

  /* No analytics prompt on the landing. A visitor from an ad decides in a
   * second or two whether to stay, and a card asking about analytics over the
   * bottom of the screen is a second decision in that time. Nothing is lost
   * by waiting: Analytics loads only after a yes either way, and the landing's
   * own visit count (landingVisit.js) needs no consent. The app asks once
   * the visitor is in. */
  if (!user) {
    return <Landing translate={translate} onOpenInfo={openInfo} deleted={justDeleted} />
  }

  if (showLanding) {
    return <Landing translate={translate} onOpenInfo={openInfo} signedIn onContinue={() => setShowLanding(false)} />
  }

  return (
    <ErrorBoundary>
      <div style={{ background: C.bg, boxSizing: 'border-box', minHeight: '100dvh', paddingTop: hideChrome ? 'var(--safe-top, 0px)' : 0, paddingBottom: `calc(${(hideChrome ? 0 : isMobile ? 56 : 0) + (showAnalyticsPrompt ? 190 : 0)}px + ${!hideChrome && isMobile ? 'var(--safe-bottom, 0px)' : '0px'})` }}>
        {hideChrome ? null : (
        <nav
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${C.faint}`,
            background: C.bg,
            padding: '0 12px',
            paddingTop: 'var(--safe-top, 0px)',
            position: 'sticky',
            top: 0,
            zIndex: 30,
          }}
        >
          <button
            onClick={() => setShowLanding(true)}
            title={translate('app.homeTitle')}
            aria-label={translate('app.home')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              minWidth: 0,
              padding: 0,
              textAlign: 'left',
            }}
          >
            <img src="/favicon.svg" alt="" style={{ width: 22, height: 22, flexShrink: 0 }} />
            <span
              style={{
                ...T.title,
                fontSize: 15.5,
                color: C.ink,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              TowardsGM
            </span>
          </button>
          {isMobile ? null : (
            <span style={{ display: 'flex', gap: 22 }}>
              {APP_TABS.map(([key, label]) => (
                <button key={key} style={tabStyle(tab === key)} onClick={() => pick(key)}>
                  {translate(label)}
                </button>
              ))}
            </span>
          )}
          <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
            {!proUser ? (
              <button
                onClick={() => setShowPro(true)}
                title={translate('app.proTitle')}
                style={{ ...chip(true), ...T.label, background: 'none', color: C.brass, minHeight: 32, whiteSpace: 'nowrap' }}
              >
                ♛{isMobile ? '' : ' PRO'}
              </button>
            ) : null}
            <AccountButton translate={translate} onOpen={() => setShowSettings(true)} />
          </span>
        </nav>
        )}
        {showSettings ? (
          <SettingsModal
            onClose={() => setShowSettings(false)}
            user={user}
            onOpenAuth={() => setShowAuth(true)}
            onOpenLicenses={() => {
              setShowSettings(false)
              openInfo('licenses')
            }}
            mobile={isMobile}
          />
        ) : null}
        {showAuth ? <AuthModal translate={translate} onClose={() => setShowAuth(false)} /> : null}
        {showPro ? (
          <ProModal
            source={handoff ? 'app_handoff' : 'nav'}
            annualFirst={Boolean(handoff)}
            checkoutReturn={checkoutReturn}
            onClose={() => {
              setShowPro(false)
              setCheckoutReturn(false)
              setHandoff(null)
            }}
          />
        ) : null}
        <Suspense fallback={<TabFallback />}>
          {/* Every tab but the Ledger is a page of the Learn tab's code, so
              switching between them keeps LearnCenter mounted: one copy of
              the progress state and its sync, whichever page is showing. */}
          {tab === 'ledger' ? <OpeningLedger onLearnLine={openLearnLine} onOpenLearn={() => pick('learn')} /> : <LearnCenter section={tab} onSection={pick} initialCourse={learnTarget} onReturnToLedger={returnToLedger} />}
        </Suspense>
        {showAnalyticsPrompt ? <AnalyticsPrompt translate={translate} raised={isMobile && !hideChrome} onChoose={chooseAnalytics} onOpenPrivacy={() => openInfo('privacy')} /> : null}
        {isMobile && !hideChrome ? (
          <nav
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              display: 'flex',
              background: C.bg,
              borderTop: `1px solid ${C.faint}`,
              paddingBottom: 'var(--safe-bottom, 0px)',
              paddingLeft: 'var(--safe-left, 0px)',
              paddingRight: 'var(--safe-right, 0px)',
            }}
          >
            {APP_TABS.map(([key, label]) => {
              const active = tab === key
              return (
                <button
                  key={key}
                  onClick={() => pick(key)}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    flex: 1,
                    background: 'none',
                    border: 'none',
                    color: active ? C.brass : C.sub,
                    cursor: 'pointer',
                    display: 'grid',
                    gap: 3,
                    justifyItems: 'center',
                    padding: '7px 0 8px',
                  }}
                >
                  {/* The active tab's icon sits in a tinted pill, the way
                      Android and iOS apps mark where you are. */}
                  <span style={{ alignItems: 'center', background: active ? TONES.accent.ground : 'transparent', borderRadius: R.pill, display: 'flex', height: 28, justifyContent: 'center', transition: 'background 0.2s ease', width: 56 }}>
                    <TabIcon name={key} />
                  </span>
                  {/* The one place the type ramp's 11.5px floor is tightened
                      rather than kept: four tab labels have to fit a 360px
                      phone, so `label`'s letter-spacing is dialled back. */}
                  <span style={{ ...T.label, fontWeight: active ? 800 : 700, letterSpacing: 0.2 }}>{translate(label)}</span>
                </button>
              )
            })}
          </nav>
        ) : null}
      </div>
    </ErrorBoundary>
  )
}

export default App
